import {
  DecedentInfo,
  extractNameFromString,
  Suffix,
} from '@sharedUtils/extract-name-from-string/extract-name-from-string';
import { AccountRegistrationsStateWithBenef } from '@store/reducers/account-registrations/account-registrations.reducer';

type NameFields = Pick<DecedentInfo, 'firstName' | 'lastName' | 'middleName' | 'suffix'>;

export function extractBenefName(regLines: string[], prefix: RegExp): NameFields {
  const benefLine: string | undefined = regLines?.find((line) => prefix?.test(line));
  return benefLine
    ? extractNameFromString(benefLine.replace(prefix!, ''))
    : { firstName: '', lastName: '', middleName: '', suffix: Suffix.None };
}

export function fuzzyNameMatch(
  decMiddle: string | null | undefined,
  benMiddle: string | null | undefined,
): boolean {
  const normalize = (name: string | null | undefined) => name?.trim().toLowerCase() || '';
  const isInitial = (name: string) => name.length === 1;
  const dec = normalize(decMiddle);
  const ben = normalize(benMiddle);

  if (!dec && !ben) return true;
  if (!dec || !ben) return true;

  if (isInitial(dec) && isInitial(ben)) return dec === ben;
  if (isInitial(dec) && ben.length > 1) return dec === ben[0];
  if (dec.length > 1 && isInitial(ben)) return dec[0] === ben;
  if (dec.length > 1 && ben.length > 1) return dec === ben;
  /* istanbul ignore next */
  throw new Error('Unreachable: all fuzzyNameMatch cases exhausted');
}

export function matchAccountsByName(
  inheritedAccounts: AccountRegistrationsStateWithBenef[],
  targetFullName: { firstName: string; lastName: string; middleName?: string },
): AccountRegistrationsStateWithBenef[] {
  return inheritedAccounts?.filter((acc) => {
    const firstNameMatch =
      acc.benefName.firstName.trim().toLowerCase() ===
      targetFullName.firstName.trim().toLowerCase();
    const lastNameMatch =
      acc.benefName.lastName.trim().toLowerCase() === targetFullName.lastName.trim().toLowerCase();
    const middleNameMatch = fuzzyNameMatch(targetFullName.middleName, acc.benefName.middleName);

    return firstNameMatch && lastNameMatch && middleNameMatch;
  });
}
